import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { lastDayOfMonth, format, getDaysInMonth, lastDayOfYear, addDays, parseISO, differenceInCalendarDays } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { actions as planActions } from './store/slice';
import PlannerModals from './components/planner-modals';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { DroppableArea, DraggableBoxes, SchedulerGrid } from './components';
import './planner-v2-view.css'; // Add this import for custom styles
import {
  fetchPlanner,
  fetchByWorkTicketId,
  fetchProcessCompletionStatus,
  actions,
} from './store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '../../core/redux/toast/slice';
import { parsePlannerDeepLink } from './planner-v2-deep-link';
import {
  selectPlannerData,
  isPlannerLoading,
  selectChannels,
  selectPrograms,
  selectFilteredPlans,
  selectPlanFilters,
} from './store/selectors';
import { fetchLocations } from '../../core/redux/location/slice';
import Filter from './components/filter';
import WeekendOnOffModal from './components/weekend-on-off-modal';
import { usePlanner } from './usePlanner';
import { PlannerService } from '../../service/api/planner-service';

const PlannerV2View = () => {
  const plannerRef = useRef(null);
  const {
    isLoading,
    getEpgProps,
    getLayoutProps,
    onChangeDate,
    calendarMode,
    dates,
    removeElement,
  } = usePlanner(plannerRef);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    (new Date().getMonth() + 1).toString().padStart(2, '0'),
  ); // Start with current month selected
  const [selectedDay, setSelectedDay] = useState('');
  const [viewMode, setViewMode] = useState('month'); // Start in month view
  const [lastAction, setLastAction] = useState('selector'); // 'selector' or 'search' - tracks which was used last
  const [centerYear, setCenterYear] = useState(new Date().getFullYear()); // Track center year for multi-year view
  // Initialize with current month range
  const currentYear = new Date().getFullYear();
  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const [start, setStart] = useState(`${currentYear}-${currentMonth}-01`);
  const [end, setEnd] = useState(
    `${currentYear}-${(new Date().getMonth() + 2).toString().padStart(2, '0')}-01`,
  );
  const [status, setStatus] = useState('All');
  const [hideEmptyMachines, setHideEmptyMachines] = useState(false);
  const [taskBoardFilterText, setTaskBoardFilterText] = useState('');

  // ========================================
  // AUTO-REFRESH CONFIGURATION
  // ========================================
  // Set to 'true' to enable automatic refresh every minute
  // Set to 'false' to disable auto-refresh
  const AUTO_REFRESH_ENABLED = true;
  // ========================================
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showWeekendOnOffModal, setShowWeekendOnOffModal] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const lastAppliedSearchRangeRef = useRef({ start: null, end: null });

  const dispatch = useDispatch();
  const location = useLocation();
  const openedDeepLinkKeyRef = useRef(null);
  const channels = useSelector(selectChannels);
  const allPrograms = useSelector(selectPrograms);
  const filteredPrograms = useSelector(selectFilteredPlans);
  const planFilters = useSelector(selectPlanFilters);

  // filteredChannels moved below smartTimelineHeader to avoid TDZ

  const handleHideEmptyMachinesChange = value => {
    setHideEmptyMachines(value);
  };

  useEffect(() => {
    dispatch(fetchPlanner());
    dispatch(fetchProcessCompletionStatus());
    dispatch(
      fetchLocations({
        name: '',
        description: '',
      }),
    );
  }, []);

  useEffect(() => {
    const deepLink = parsePlannerDeepLink(location.search);
    if (!deepLink) {
      openedDeepLinkKeyRef.current = null;
      return undefined;
    }

    const key = `${deepLink.processId || ''}:${deepLink.workTicket || ''}`;
    if (openedDeepLinkKeyRef.current === key) {
      return undefined;
    }

    const id = deepLink.processId || (/^\d+$/.test(deepLink.workTicket) ? deepLink.workTicket : '');
    if (!id) {
      dispatch(
        showToast({
          message: 'Could not open the work ticket from this notification.',
          type: 'warning',
        }),
      );
      openedDeepLinkKeyRef.current = key;
      return undefined;
    }

    dispatch(fetchByWorkTicketId(id)).then(result => {
      if (result?.error) {
        dispatch(
          showToast({
            message: 'Could not open the work ticket from this notification.',
            type: 'warning',
          }),
        );
      }
    });
    openedDeepLinkKeyRef.current = key;
    return undefined;
  }, [dispatch, location.search]);

  // When search has results, update timeline to show the date range of matches so bars are visible and scroll works.
  // When search text is cleared, fall back to selector-based timeline (year/month/day) so planner is not stuck in search mode.
  useEffect(() => {
    const searchText = planFilters?.searchText?.trim() || '';

    // If search text is cleared, go back to selector mode and do nothing else
    if (searchText === '') {
      setLastAction('selector');
      return;
    }

    if (!filteredPrograms?.length) return;

    const sinceDates = filteredPrograms.filter(p => p.since).map(p => parseISO(p.since));
    const tillDates = filteredPrograms
      .filter(p => p.since)
      .map(p => {
        const t = p.till && String(p.till).trim() !== '' ? parseISO(p.till) : parseISO(p.since);
        return t;
      })
      .filter(d => d && !Number.isNaN(d.getTime()));
    if (sinceDates.length === 0 || tillDates.length === 0) return;

    const minSince = new Date(Math.min(...sinceDates.map(d => d.getTime())));
    const maxTill = new Date(Math.max(...tillDates.map(d => d.getTime())));
    const rangeStart = format(minSince, 'yyyy-MM-dd');
    const rangeEnd = format(maxTill, 'yyyy-MM-dd');
    const startDate = `${rangeStart}T00:00:00`;
    const endDate = format(addDays(maxTill, 1), "yyyy-MM-dd'T'00:00:00");

    // Prevent update loops: normalize start/end to date-only and short-circuit if unchanged.
    const currentStart = planFilters?.start ? String(planFilters.start).slice(0, 10) : '';
    const currentEnd = planFilters?.end ? String(planFilters.end).slice(0, 10) : '';
    const lastApplied = lastAppliedSearchRangeRef.current || {};
    if (
      currentStart === rangeStart &&
      currentEnd === rangeEnd &&
      lastAction === 'search' &&
      lastApplied.start === rangeStart &&
      lastApplied.end === rangeEnd
    ) {
      return;
    }

    lastAppliedSearchRangeRef.current = { start: rangeStart, end: rangeEnd };
    if (lastAction !== 'search') setLastAction('search');
    setStart(startDate);
    setEnd(endDate);
    onChangeDate(startDate, endDate, 'week');
    handleStartEndDateChange(startDate, endDate);
  }, [planFilters?.searchText, filteredPrograms]);

  // Auto-refresh functionality - refreshes every minute when enabled
  useEffect(() => {
    if (AUTO_REFRESH_ENABLED) {
      // Set up interval to refresh every minute (60000ms)
      const interval = setInterval(async () => {
        console.log('Auto-refreshing planner data...', new Date().toLocaleTimeString());
        setIsAutoRefreshing(true);

        try {
          await dispatch(
            fetchPlanner({
              searchText: planFilters.searchText,
              status: planFilters.status,
              start: planFilters.start,
              end: planFilters.end,
            }),
          ).unwrap();
          await dispatch(fetchProcessCompletionStatus()).unwrap();

          setLastRefreshTime(new Date());
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        } finally {
          // Hide loading indicator after 1 second
          setTimeout(() => {
            setIsAutoRefreshing(false);
          }, 1000);
        }
      }, 60000); // 60 seconds

      // Cleanup interval on component unmount
      return () => {
        clearInterval(interval);
      };
    }
  }, [AUTO_REFRESH_ENABLED, dispatch, planFilters]);

  // Zoomed fullscreen toggle functionality
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const generateYearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear + 2;
    const endYear = currentYear - 5;
    return Array.from({ length: startYear - endYear + 1 }, (_, index) => startYear - index);
  }, []);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const date = new Date(2000, index, 1); // Using 2000 as dummy year
      return {
        value: month.toString().padStart(2, '0'),
        label: format(date, 'MMMM'),
      };
    });
  }, []);

  const days = useMemo(() => {
    if (!selectedYear || !selectedMonth) return [];
    const daysInMonth = getDaysInMonth(new Date(selectedYear, parseInt(selectedMonth) - 1));
    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      return {
        value: day.toString().padStart(2, '0'),
        label: day.toString().padStart(2, '0'),
      };
    });
  }, [selectedYear, selectedMonth]);

  // Smart timeline header based on date range
  const timelineHeader = useMemo(() => {
    if (!selectedYear) {
      // Years view - show multiple years when no year is selected
      const baseYear = centerYear; // Use centerYear for navigation
      const startYear = baseYear - 2; // Show 2 years before
      const endYear = baseYear + 3; // Show 3 years after
      const yearsToShow = endYear - startYear + 1;

      return Array.from({ length: yearsToShow }, (_, index) => {
        const year = startYear + index;
        return {
          label: year.toString(),
          year: year,
          iso: `${year}-01-01`,
          endIso: `${year}-12-31`,
          viewMode: 'year-multi',
        };
      });
    } else if (selectedDay) {
      // Day view - show hourly timeline (00:00 - 23:00)
      const selectedDate = new Date(
        selectedYear,
        parseInt(selectedMonth) - 1,
        parseInt(selectedDay),
      );
      const dateStr = format(selectedDate, 'yyyy-MM-dd');

      return Array.from({ length: 24 }, (_, hour) => {
        return {
          label: `${hour.toString().padStart(2, '0')}:00`,
          hour: hour,
          iso: `${dateStr}T${hour.toString().padStart(2, '0')}:00:00`,
          date: dateStr,
          viewMode: 'hour',
        };
      });
    } else if (selectedMonth) {
      // Month view - show individual days within the selected month
      const daysInMonth = getDaysInMonth(new Date(selectedYear, parseInt(selectedMonth) - 1));
      return Array.from({ length: daysInMonth }, (_, index) => {
        const day = index + 1;
        const date = new Date(selectedYear, parseInt(selectedMonth) - 1, day);
        return {
          label: format(date, 'EEE dd'),
          iso: format(date, 'yyyy-MM-dd'),
          viewMode: 'day',
        };
      });
    } else {
      // Year view (no month selected) - show months (Jan, Feb, Mar, etc.)
      return Array.from({ length: 12 }, (_, index) => {
        const date = new Date(selectedYear, index, 1);
        const lastDay = lastDayOfMonth(date);
        return {
          label: format(date, 'MMM'),
          year: selectedYear,
          iso: format(date, 'yyyy-MM-dd'),
          endIso: format(lastDay, 'yyyy-MM-dd'),
          viewMode: 'month',
          monthIndex: index,
        };
      });
    }
  }, [selectedYear, selectedMonth, selectedDay, centerYear]);

  // Timeline for filter dates - respect the last action (search or selector)
  const smartTimelineHeader = useMemo(() => {
    // If last action was using year/month/day selectors, use selector-based timeline
    if (lastAction === 'selector') {
      return timelineHeader;
    }

    // If last action was search, show day-based timeline with the searched date range (inclusive)
    // Use local component state `start/end` (we intentionally do not persist these to Redux during live keyword typing).
    if (lastAction === 'search' && start && end) {
      const startDate = parseISO(String(start).slice(0, 10));
      const endDate = parseISO(String(end).slice(0, 10));

      // Inclusive day count (e.g. 2nd–20th => 19 days)
      const rawDays = differenceInCalendarDays(endDate, startDate) + 1;
      const daysDiff = Math.max(1, Math.min(31, rawDays)); // cap to 31 columns

      // Show individual days with weekday format (Mon 05, Tue 06, etc.)
      return Array.from({ length: daysDiff }, (_, index) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + index);
        return {
          label: format(date, 'EEE dd'),
          dow: format(date, 'EEE'),
          day: format(date, 'dd'),
          iso: format(date, 'yyyy-MM-dd'),
          viewMode: 'day',
        };
      });
    }

    // Fallback to timelineHeader
    return timelineHeader;
  }, [start, end, timelineHeader, lastAction]);

  // Helper function to parse date strings in local timezone (avoid UTC conversion issues)
  const parseLocalDate = dateStr => {
    if (!dateStr) return null;
    // If it's already a date-only string (yyyy-MM-dd), parse it as local midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    // Otherwise, use parseISO and convert to local
    try {
      const date = parseISO(dateStr);
      // Return as local date (midnight local time)
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    } catch {
      return null;
    }
  };

  // Use local parsing for yyyy-MM-dd to avoid timezone shifts (e.g. Texas showing previous day/month).
  const parseYmdOrLocal = dateStr => parseLocalDate(dateStr) || new Date(dateStr);

  // Compute the set of programs to render based on the visible timeline
  const effectivePrograms = useMemo(() => {
    if (smartTimelineHeader && smartTimelineHeader.length > 0) {
      const first = smartTimelineHeader[0];
      const last = smartTimelineHeader[smartTimelineHeader.length - 1];
      const startIso = first.iso || first.date;
      // For month view, use endIso if available (last day of last month), otherwise use iso
      const endIso = last.endIso || last.iso || last.date;

      if (startIso && endIso) {
        const vs = parseLocalDate(startIso);
        // For month view, endIso is the last day of the last month (e.g., "2025-12-31")
        // Parse it and ensure it includes the full day
        let ve = parseLocalDate(endIso);
        if (!ve) {
          // Fallback: compute end date from start
          ve = new Date(vs);
          ve.setMonth(ve.getMonth() + 12); // Add 12 months for year view
        }
        // Set to end of day to include the entire last day
        ve.setHours(23, 59, 59, 999);

        // Debug: Log timeline header info for year view
        if (smartTimelineHeader[0]?.viewMode === 'month' && smartTimelineHeader.length === 12) {
          console.log('[Year View Debug] Timeline Header:', {
            headerLength: smartTimelineHeader.length,
            firstMonth: smartTimelineHeader[0],
            lastMonth: smartTimelineHeader[11],
            startIso: startIso,
            endIso: endIso,
            timelineStart: vs.toISOString(),
            timelineEnd: ve.toISOString(),
            selectedYear: selectedYear,
            selectedMonth: selectedMonth,
            viewMode: viewMode,
            lastAction: lastAction,
          });
        }

        const activeStatus = planFilters?.status;
        const isStatusEnabled = activeStatus && activeStatus !== 'All';

        return allPrograms.filter(p => {
          if (!p?.since || !p?.till) return false;
          const ps = parseLocalDate(p.since.split('T')[0]); // Use date part only
          const pe = parseLocalDate(p.till.split('T')[0]); // Use date part only

          if (!ps || !pe) return false;

          // Set program end to end of day
          const peEndOfDay = new Date(pe);
          peEndOfDay.setHours(23, 59, 59, 999);

          // Check if program overlaps with visible timeline
          // Program overlaps if: program starts before timeline ends AND program ends after timeline starts
          const overlaps = ps <= ve && peEndOfDay >= vs;

          // Apply status filter when enabled
          const matchesStatus = isStatusEnabled ? p.status === activeStatus : true;

          // Debug logging for specific program (id: 1444)
          if (p.id === '1444' || p.id === 1444) {
            console.log('[Month View Debug] Program 1444:', {
              programSince: p.since,
              programTill: p.till,
              parsedSince: ps.toISOString(),
              parsedTill: peEndOfDay.toISOString(),
              timelineStart: vs.toISOString(),
              timelineEnd: ve.toISOString(),
              overlaps: overlaps,
              startIso: startIso,
              endIso: endIso,
              smartTimelineHeaderLength: smartTimelineHeader.length,
              smartTimelineHeaderFirst: smartTimelineHeader[0],
              smartTimelineHeaderLast: smartTimelineHeader[smartTimelineHeader.length - 1],
            });
          }

          return overlaps && matchesStatus;
        });
      }
    }
    return filteredPrograms;
  }, [
    smartTimelineHeader,
    allPrograms,
    filteredPrograms,
    selectedYear,
    selectedMonth,
    viewMode,
    lastAction,
    planFilters?.status,
  ]);

  // Set of program ids that match the current search (for highlighting bars)
  const searchMatchedProgramIds = useMemo(() => {
    const text = planFilters?.searchText?.trim() || '';
    if (text === '' || !filteredPrograms?.length) return new Set();
    const ids = new Set();
    filteredPrograms.forEach(p => {
      if (p.id != null) ids.add(String(p.id));
      if (p.originalId != null) ids.add(String(p.originalId));
    });
    return ids;
  }, [planFilters?.searchText, filteredPrograms]);

  // Filter channels based on hideEmptyMachines setting
  // Uses the actual visible timeline (smartTimelineHeader) to decide allocations
  const filteredChannels = useMemo(() => {
    if (!hideEmptyMachines) return channels;

    const keyOfChannel = c =>
      c?.uuid || c?.id || c?.assetNumber || c?.title || String(c?.index || '');

    // Determine visible range from smartTimelineHeader (what user actually sees)
    let visibleStartDate = null;
    let visibleEndDate = null;
    if (smartTimelineHeader && smartTimelineHeader.length > 0) {
      const first = smartTimelineHeader[0];
      const last = smartTimelineHeader[smartTimelineHeader.length - 1];
      const firstIso = first.iso || first.date;
      const lastIso = last.endIso || last.iso || last.date;
      if (firstIso && lastIso) {
        visibleStartDate = new Date(firstIso);
        visibleEndDate = new Date(lastIso);
        visibleEndDate.setHours(23, 59, 59, 999);
      }
    }

    if (!visibleStartDate || !visibleEndDate) {
      if (start && end) {
        visibleStartDate = new Date(start);
        visibleEndDate = new Date(end);
      }
    }

    // Build set of program keys that overlap the visible range
    const programKeys = new Set();
    effectivePrograms.forEach(program => {
      if (visibleStartDate && visibleEndDate && program.since && program.till) {
        const ps = new Date(program.since);
        const pe = new Date(program.till);
        const overlaps = ps <= visibleEndDate && pe >= visibleStartDate;
        if (!overlaps) return;
      }
      [
        program.channelUuid,
        program.channelId,
        program.assetNumber,
        program.channel,
        program.machineId,
      ]
        .filter(Boolean)
        .map(String)
        .forEach(id => {
          if (id && id !== 'undefined' && id !== '') programKeys.add(id);
        });
    });

    return channels.filter((channel, index) => {
      const channelIdentifiers = [
        channel.uuid,
        channel.id,
        channel.assetNumber,
        channel.title,
        keyOfChannel(channel),
        keyOfChannel({ ...channel, index }),
      ]
        .filter(Boolean)
        .map(String);
      return channelIdentifiers.some(id => programKeys.has(id));
    });
  }, [channels, effectivePrograms, hideEmptyMachines, smartTimelineHeader, start, end]);

  const handleYearUnselect = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setViewMode('years'); // Set to multi-year view
    setLastAction('selector');
    const currentYear = new Date().getFullYear();
    setCenterYear(currentYear); // Track the center year for navigation
    const startYear = currentYear - 2;
    const endYear = currentYear + 3;
    const yearsStart = `${startYear}-01-01`;
    const yearsEnd = `${endYear}-12-31`;
    setStart(`${yearsStart}T00:00:00`);
    setEnd(`${yearsEnd}T24:00:00`);
  };

  const handleYearChange = event => {
    const year = event.target.value;
    if (year === '') {
      handleYearUnselect();
      return;
    }
    setSelectedYear(year);
    setSelectedMonth(''); // Reset month
    setSelectedDay(''); // Reset day
    setViewMode('year'); // Set to year view
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${year}-01-01`;
    // Explicitly set the date range to cover the entire year
    const yearStart = `${year}-01-01`;
    const yearEnd = format(lastDayOfYear(new Date(year, 0, 1)), 'yyyy-MM-dd');
    const yearStartISO = `${yearStart}T00:00:00`;
    const yearEndISO = `${yearEnd}T24:00:00`;
    setStart(yearStartISO);
    setEnd(yearEndISO);

    // Update Redux filter state to ensure all programs in the year are shown
    dispatch(
      actions.setFilters({
        start: yearStart,
        end: yearEnd,
        status: status,
      }),
    );

    handleTimelineClick(startDate, 'year');
  };

  const handleMonthUnselect = () => {
    setSelectedMonth('');
    setSelectedDay('');
    setViewMode('year'); // Set to year view when month is unselected
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-01-01`;
    // Explicitly set the date range to cover the entire year
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = format(lastDayOfYear(new Date(selectedYear, 0, 1)), 'yyyy-MM-dd');
    const yearStartISO = `${yearStart}T00:00:00`;
    const yearEndISO = `${yearEnd}T24:00:00`;
    setStart(yearStartISO);
    setEnd(yearEndISO);

    // Update Redux filter state to ensure all programs in the year are shown
    dispatch(
      actions.setFilters({
        start: yearStart,
        end: yearEnd,
        status: status,
      }),
    );

    handleTimelineClick(startDate, 'year');
  };

  const handleMonthChange = event => {
    const month = event.target.value;
    if (month === '') {
      handleMonthUnselect();
      return;
    }
    setSelectedMonth(month);
    setSelectedDay('');
    setViewMode('month'); // Set to month view (shows days)
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${month}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayUnselect = () => {
    setSelectedDay('');
    setViewMode('month'); // Back to month view
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${selectedMonth}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayChange = event => {
    const day = event.target.value;
    if (day === '') {
      handleDayUnselect();
      return;
    }
    setSelectedDay(day);
    setViewMode('day'); // Set to day view (shows week)
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${selectedMonth}-${day}`;
    handleTimelineClick(startDate, 'week');
  };

  const handleTimelineClick = (date, type) => {
    let startDate, endDate;
    if (type === 'month') {
      startDate = `${date}T00:00:00`;
      endDate = format(
        new Date(new Date(startDate).setMonth(new Date(startDate).getMonth() + 1)),
        "yyyy-MM-dd'T'24:00:00",
      );
      setSelectedMonth(format(parseYmdOrLocal(date), 'MM'));
      setSelectedDay('');
      onChangeDate(startDate, endDate, 'week');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'week') {
      startDate = `${date}T00:00:00`;
      endDate = `${date}T24:00:00`;
      const day = format(parseYmdOrLocal(date), 'dd');
      setSelectedDay(day);
      onChangeDate(startDate, endDate, 'day');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'year') {
      startDate = `${date}T00:00:00`;
      endDate = format(lastDayOfYear(new Date(startDate)), "yyyy-MM-dd'T'24:00:00");
      onChangeDate(startDate, endDate, 'month');
      handleStartEndDateChange(startDate, endDate);
    }
  };

  const handleStartEndDateChange = (start, end) => {
    setStart(start);
    setEnd(end);
  };

  const handleSearch = (fromDate, toDate, currentStatus) => {
    setStatus(currentStatus);
    setLastAction('search'); // Mark that search was used - this takes priority

    // If To date is empty/null, treat it as the same as From date (single-day search)
    const effectiveToDate = toDate && toDate !== '' ? toDate : fromDate;

    // Timeline range for useDays (end is exclusive, so we add +1 day)
    const timelineStartIso = `${fromDate}T00:00:00`;
    const timelineEndExclusiveIso = format(
      addDays(parseYmdOrLocal(effectiveToDate), 1),
      "yyyy-MM-dd'T'00:00:00",
    );

    // Filter state range we want to persist/display (inclusive, no +1 day)
    const filterStartIso = `${fromDate}T00:00:00`;
    const filterEndIso = `${effectiveToDate}T00:00:00`;

    // Update Redux filter state
    dispatch(
      actions.setFilters({
        start: fromDate,
        end: effectiveToDate,
        status: currentStatus,
      }),
    );

    // Update timeline (uses exclusive end) and local start/end (for Filter inputs)
    onChangeDate(timelineStartIso, timelineEndExclusiveIso, 'week');
    handleStartEndDateChange(filterStartIso, filterEndIso);
  };

  const handleProgramEdit = id => {
    dispatch(fetchByWorkTicketId(id));
  };

  // Navigation logic for timeline arrows - respects current view mode
  const handleTimelineNavigation = direction => {
    let newYear, newMonth, newDay;
    const multiplier = direction === 'prev' ? -1 : 1;

    if (viewMode === 'years') {
      // Multi-year view - navigate by 6 years (the span shown)
      newYear = centerYear + multiplier * 6;
      setCenterYear(newYear); // Update center year for navigation
      setSelectedYear(''); // Keep year unselected
      setSelectedMonth('');
      setSelectedDay('');
      const yearsStart = `${newYear - 2}-01-01`;
      const yearsEnd = `${newYear + 3}-12-31`;
      setStart(`${yearsStart}T00:00:00`);
      setEnd(`${yearsEnd}T24:00:00`);
      setLastAction('selector');
    } else if (viewMode === 'year') {
      // Yearly view - navigate by 1 year
      newYear = parseInt(selectedYear) + multiplier;
      setSelectedYear(newYear);
      const yearStart = `${newYear}-01-01`;
      const yearEnd = format(lastDayOfYear(new Date(newYear, 0, 1)), 'yyyy-MM-dd');
      setStart(`${yearStart}T00:00:00`);
      setEnd(`${yearEnd}T24:00:00`);
      handleTimelineClick(yearStart, 'year');
    } else if (viewMode === 'month') {
      // Monthly view - navigate by 1 month
      let monthNum = parseInt(selectedMonth) + multiplier;
      let yearNum = parseInt(selectedYear);

      if (monthNum > 12) {
        monthNum = 1;
        yearNum++;
      } else if (monthNum < 1) {
        monthNum = 12;
        yearNum--;
      }

      setSelectedYear(yearNum);
      setSelectedMonth(monthNum.toString().padStart(2, '0'));
      const monthStart = `${yearNum}-${monthNum.toString().padStart(2, '0')}-01`;
      handleTimelineClick(monthStart, 'month');
    } else if (viewMode === 'day' || viewMode === 'hour') {
      // Day/Hour view - navigate by 1 day
      const currentDate = new Date(
        selectedYear,
        parseInt(selectedMonth) - 1,
        parseInt(selectedDay),
      );
      const newDate = addDays(currentDate, multiplier);

      setSelectedYear(newDate.getFullYear());
      setSelectedMonth((newDate.getMonth() + 1).toString().padStart(2, '0'));
      setSelectedDay(newDate.getDate().toString().padStart(2, '0'));
      const dayStart = format(newDate, 'yyyy-MM-dd');
      handleTimelineClick(dayStart, 'week');
    } else {
      // Fallback to old behavior for search mode
      const currentStart = start || new Date().toISOString();
      const currentEnd = end || new Date(Date.now() + 24 * 3600 * 1000).toISOString();
      const daysDiff = Math.ceil(
        (new Date(currentEnd) - new Date(currentStart)) / (1000 * 60 * 60 * 24),
      );

      const newStart = format(addDays(new Date(currentStart), multiplier * daysDiff), 'yyyy-MM-dd');
      const newEnd = format(addDays(new Date(currentEnd), multiplier * daysDiff), 'yyyy-MM-dd');

      handleSearch(newStart, newEnd, status);
    }
  };

  // Handle header cell click to drill into year/month/day views (no Planby)
  const handleHeaderSelection = header => {
    if (!header) return;
    setLastAction('selector');

    // If the header contains a specific viewMode, use it to decide behavior
    const headerView = header.viewMode || viewMode;

    try {
      if (headerView === 'year-multi' && header.year) {
        // Click on a year cell in multi-year view -> go to that year
        handleYearChange({ target: { value: header.year } });
        return;
      }

      if (headerView === 'month') {
        // Click on a month cell -> go to that month (day view of that month)
        const iso = header.iso || header.label;
        if (iso) {
          const dateObj = parseISO(iso);
          const yr = format(dateObj, 'yyyy');
          const mo = format(dateObj, 'MM');
          setSelectedYear(yr);
          setSelectedMonth(mo);
          setSelectedDay('');
          setViewMode('month');
          handleTimelineClick(`${yr}-${mo}-01`, 'month');
        }
        return;
      }

      // Default / day view click -> go to day/hour view
      const iso = header.iso || header.date;
      if (iso) {
        const dateObj = parseISO(iso);
        const yr = format(dateObj, 'yyyy');
        const mo = format(dateObj, 'MM');
        const dy = format(dateObj, 'dd');
        setSelectedYear(yr);
        setSelectedMonth(mo);
        setSelectedDay(dy);
        setViewMode('day');
        handleTimelineClick(`${yr}-${mo}-${dy}`, 'week');
      }
    } catch (e) {
      console.warn('Failed to handle header selection', e);
    }
  };

  return (
    <>
      <div
        style={{
          height: 'auto',
          display: isFullScreen ? 'none' : 'block',
        }}
      >
        <div className="page-wrapper" style={{ height: 'auto' }}>
          <div
            className={`content`}
            style={{
              height: 'auto',
              display: 'block',
            }}
          >
            <div className="page-header transfer">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Production planning</h4>
                </div>
              </div>
              {/* Weekend ON/OFF + Fullscreen */}
              <div className="d-flex justify-content-end align-items-center mb-3 planner-meta-row gap-2">
                <button
                  type="button"
                  className="btn btn-weekend-on-off btn-sm"
                  onClick={() => setShowWeekendOnOffModal(true)}
                  title="Manage Weekend ON/OFF for calendar tasks"
                >
                  <i className="fas fa-calendar-week"></i>
                  {' Weekend ON/OFF'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={toggleFullScreen}
                  title="Enter Zoomed Fullscreen View"
                >
                  <i className="fas fa-expand"></i>
                  {' Fullscreen'}
                </button>
              </div>
            </div>

            <DndProvider backend={HTML5Backend}>
              <div ref={plannerRef} className={selectedDay ? 'day-selected' : ''}>
                <div className="planner-row planner-row--two mb-2">
                  {/* Container 1: Draggable Boxes + Task search */}
                  <div
                    className="planner-card planner-card--wide planner-card--task-board"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1.6 1 0',
                      minHeight: 0,
                    }}
                  >
                    <div className="planner-card-header">
                      <h6 className="planner-card-title mb-0">Task board</h6>
                      <div
                        className="input-blocks draggable-input-box-filter mb-0"
                        style={{ width: 260 }}
                      >
                        <input
                          type="text"
                          placeholder="Enter task name or ID"
                          value={taskBoardFilterText}
                          onChange={e => setTaskBoardFilterText(e.target.value)}
                          className="form-control"
                          style={{ height: '52px' }}
                        />
                      </div>
                    </div>
                    <div className="task-board-body">
                      <DraggableBoxes
                        filterText={taskBoardFilterText}
                        onFilterTextChange={setTaskBoardFilterText}
                        showFilterInput={false}
                      />
                    </div>
                  </div>

                  {/* Container 2 & 3 inside Filter component */}
                  <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0 }}>
                    <Filter
                      onHandleSearch={handleSearch}
                      start={start}
                      end={end}
                      hideEmptyMachines={hideEmptyMachines}
                      onHideEmptyMachinesChange={handleHideEmptyMachinesChange}
                      // Date selector props
                      selectedYear={selectedYear}
                      selectedMonth={selectedMonth}
                      selectedDay={selectedDay}
                      generateYearsList={generateYearsList}
                      months={months}
                      days={days}
                      onYearChange={handleYearChange}
                      onMonthChange={handleMonthChange}
                      onDayChange={handleDayChange}
                      onYearUnselect={handleYearUnselect}
                      onMonthUnselect={handleMonthUnselect}
                      onDayUnselect={handleDayUnselect}
                    />
                  </div>
                </div>
                {/* Auto-refresh indicator, back-to-calendar button, and checkbox */}
                <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                  {AUTO_REFRESH_ENABLED && (
                    <div className="d-flex flex-column gap-1">
                      {isAutoRefreshing ? (
                        <div className="d-flex align-items-center gap-2 text-primary">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Refreshing...</span>
                          </div>
                          <span className="fw-bold">Refreshing planner data...</span>
                        </div>
                      ) : (
                        <>
                          {lastRefreshTime && (
                            <small className="text-muted">
                              <i className="fas fa-check-circle text-success"></i> Last refreshed:{' '}
                              {lastRefreshTime.toLocaleTimeString()}
                            </small>
                          )}
                          {selectedDay && (
                            <div>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm mt-1"
                                onClick={handleDayUnselect}
                                title="Back to calendar view"
                              >
                                <i className="fas fa-arrow-left me-1"></i>
                                Back
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="form-check d-flex align-items-center mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideEmptyMachines"
                      checked={hideEmptyMachines}
                      onChange={e => handleHideEmptyMachinesChange(e.target.checked)}
                      style={{ width: '18px', height: '18px', marginRight: '8px', flexShrink: 0 }}
                    />
                    <label
                      className="form-check-label mb-0"
                      htmlFor="hideEmptyMachines"
                      style={{ cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      Don't show non allocated machines
                    </label>
                  </div>
                </div>
                <div
                  style={{
                    height: '68vh',
                    width: '100%',
                    overflowY: 'auto',
                  }}
                >
                  <DroppableArea removeElement={removeElement}>
                    <div style={{ marginBottom: 16 }}>
                      <SchedulerGrid
                        channels={filteredChannels}
                        programs={effectivePrograms}
                        dates={dates}
                        timelineHeader={smartTimelineHeader}
                        filterDates={lastAction === 'search' ? { start, end } : null}
                        timelineWidth={
                          plannerRef.current ? plannerRef.current.offsetWidth - 16 : 1000
                        }
                        onProgramEdit={handleProgramEdit}
                        sidebarWidth={45}
                        showHeaderLabel={false}
                        onNavigateTimeline={handleTimelineNavigation}
                        onHeaderClick={handleHeaderSelection}
                        viewMode={viewMode}
                        searchMatchedProgramIds={searchMatchedProgramIds}
                      />
                    </div>
                  </DroppableArea>
                </div>
              </div>
            </DndProvider>
          </div>
        </div>
        <PlannerModals />
      </div>

      {/* Zoomed Fullscreen Overlay */}
      {isFullScreen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#ededed',
            zIndex: 9999,
            overflow: 'auto',
            padding: '20px',
          }}
        >
          {/* Zoomed Content */}
          <div
            style={{
              transformOrigin: 'top center',
              margin: '0 auto',
            }}
          >
            <div className="page-header transfer">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Production planning</h4>
                </div>
              </div>

              {/* Auto-refresh indicator, checkbox, and Fullscreen Control */}
              <div className="d-flex justify-content-between align-items-center mb-3 planner-meta-row flex-wrap gap-3">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-weekend-on-off btn-sm"
                    onClick={() => setShowWeekendOnOffModal(true)}
                    title="Manage Weekend ON/OFF for calendar tasks"
                  >
                    <i className="fas fa-calendar-week"></i>
                    {' Weekend ON/OFF'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={toggleFullScreen}
                    title="Enter Zoomed Fullscreen View"
                  >
                    <i className="fas fa-expand"></i>
                    {' Exit Fullscreen'}
                  </button>
                </div>
              </div>
            </div>

            <DndProvider backend={HTML5Backend}>
              <div ref={plannerRef} className={selectedDay ? 'day-selected' : ''}>
                <div className="planner-row planner-row--two mb-2">
                  {/* Container 1: Draggable Boxes + Task search */}
                  <div
                    className="planner-card planner-card--wide planner-card--task-board"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1.6 1 0',
                      minHeight: 0,
                    }}
                  >
                    <div className="planner-card-header">
                      <h6 className="planner-card-title mb-0">Task board</h6>
                      <div
                        className="input-blocks draggable-input-box-filter mb-0"
                        style={{ width: 260 }}
                      >
                        <input
                          type="text"
                          placeholder="Enter task name or ID"
                          value={taskBoardFilterText}
                          onChange={e => setTaskBoardFilterText(e.target.value)}
                          className="form-control"
                          style={{ height: '52px' }}
                        />
                      </div>
                    </div>
                    <div className="task-board-body">
                      <DraggableBoxes
                        filterText={taskBoardFilterText}
                        onFilterTextChange={setTaskBoardFilterText}
                        showFilterInput={false}
                      />
                    </div>
                  </div>

                  {/* Container 2 & 3 inside Filter component */}
                  <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0 }}>
                    <Filter
                      onHandleSearch={handleSearch}
                      start={start}
                      end={end}
                      hideEmptyMachines={hideEmptyMachines}
                      onHideEmptyMachinesChange={handleHideEmptyMachinesChange}
                      // Date selector props
                      selectedYear={selectedYear}
                      selectedMonth={selectedMonth}
                      selectedDay={selectedDay}
                      generateYearsList={generateYearsList}
                      months={months}
                      days={days}
                      onYearChange={handleYearChange}
                      onMonthChange={handleMonthChange}
                      onDayChange={handleDayChange}
                      onYearUnselect={handleYearUnselect}
                      onMonthUnselect={handleMonthUnselect}
                      onDayUnselect={handleDayUnselect}
                    />
                  </div>
                </div>

{/* Auto-refresh indicator, back-to-calendar button, and checkbox */}
                <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                  {AUTO_REFRESH_ENABLED && (
                    <div className="d-flex flex-column gap-1">
                      {isAutoRefreshing ? (
                        <div className="d-flex align-items-center gap-2 text-primary">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Refreshing...</span>
                          </div>
                          <span className="fw-bold">Refreshing planner data...</span>
                        </div>
                      ) : (
                        <>
                          {lastRefreshTime && (
                            <small className="text-muted">
                              <i className="fas fa-check-circle text-success"></i> Last refreshed:{' '}
                              {lastRefreshTime.toLocaleTimeString()}
                            </small>
                          )}
                          {selectedDay && (
                            <div>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm mt-1"
                                onClick={handleDayUnselect}
                                title="Back to calendar view"
                              >
                                <i className="fas fa-arrow-left me-1"></i>
                                Back
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="form-check d-flex align-items-center mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideEmptyMachines"
                      checked={hideEmptyMachines}
                      onChange={e => handleHideEmptyMachinesChange(e.target.checked)}
                      style={{ width: '18px', height: '18px', marginRight: '8px', flexShrink: 0 }}
                    />
                    <label
                      className="form-check-label mb-0"
                      htmlFor="hideEmptyMachines"
                      style={{ cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      Don't show non allocated machines
                    </label>
                  </div>
                </div>
                
                <div
                  style={{
                    height: '68vh',
                    width: '100%',
                    overflowY: 'auto',
                  }}
                >
                  <DroppableArea removeElement={removeElement}>
                    <div style={{ marginBottom: 16 }}>
                      <SchedulerGrid
                        channels={filteredChannels}
                        programs={effectivePrograms}
                        dates={dates}
                        timelineHeader={smartTimelineHeader}
                        filterDates={lastAction === 'search' ? { start, end } : null}
                        timelineWidth={
                          plannerRef.current ? plannerRef.current.offsetWidth - 16 : 1000
                        }
                        onProgramEdit={handleProgramEdit}
                        sidebarWidth={45}
                        showHeaderLabel={false}
                        onNavigateTimeline={handleTimelineNavigation}
                        onHeaderClick={handleHeaderSelection}
                        viewMode={viewMode}
                        searchMatchedProgramIds={searchMatchedProgramIds}
                      />
                    </div>
                  </DroppableArea>
                </div>
              </div>
            </DndProvider>
          </div>
        </div>
      )}

      <WeekendOnOffModal
        show={showWeekendOnOffModal}
        onClose={() => setShowWeekendOnOffModal(false)}
      />
    </>
  );
};

export default PlannerV2View;
